import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import Swal from "sweetalert2";
import { Search, Plus } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DropdownOption } from "../../types/dropdown";
import Dropdown from "../../components/dropdown/Index";
import useMallTenantDropdown from "../../hooks/useMallTenantDropdown";
import {
  GetRequestStatusTenant,
  fetchdropdownMall,
  fetchdropdownTenant,
} from "../../services/dropdown";
import {
  GetRequestTenant,
  SaveRequestTenant,
} from "../../services/tenantrequest";
import { toLocalISOString, formatStatusLabel } from "../../utils";
import TenantRequestStats from "../../components/tenantrequest/TenantRequestStats";
import TenantRequestTable from "../../components/tenantrequest/TenantRequestTable";
import AddRequestModal from "../../components/tenantrequest/AddRequestModal";

type SortKey =
  | "RequestNumber"
  | "MallName"
  | "TenantName"
  | "RequestStatusName";

interface TenantRequestData {
  TenantRequestId: number;
  RequestNumber: string;
  MallName: string;
  TenantName: string;
  Notes: string;
  RequestStatusName?: string;
  RequestStatusId?: number;
  Status: number;
  CreatedOn?: string;
}

interface ImageData {
  TenantRequestImageId: number;
  TenantRequestId: number;
  ImageTypeId: number;
  ImageTypeName: string;
  FileImage: string;
  DataImage: string;
  ImageLabel: string;
  Status: number;
}

interface RawStatusData {
  DataId?: number;
  dataId?: number;
  value?: number;
  DataLabel?: string;
  dataLabel?: string;
  label?: string;
}

export default function TenantRequest() {
  const [requestStatusOptions, setRequestStatusOptions] = useState<
    DropdownOption[]
  >([]);
  const [tenantRequestData, setTenantRequestData] = useState<
    TenantRequestData[]
  >([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedRequestStatus, setSelectedRequestStatus] = useState<
    number | null
  >(null);
  const [sortKey, setSortKey] = useState<SortKey>("RequestNumber");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [taskId] = useState<number>(0);
  const [tenantRequestId] = useState<number>(0);
  const [numberParam] = useState<number>(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalMall, setAddModalMall] = useState<number | null>(null);
  const [addModalTenant, setAddModalTenant] = useState<number | null>(null);
  const [addModalNotes, setAddModalNotes] = useState("");
  const [addModalImages, setAddModalImages] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalMallOptions, setModalMallOptions] = useState<DropdownOption[]>(
    []
  );
  const [modalTenantOptions, setModalTenantOptions] = useState<
    DropdownOption[]
  >([]);
  const [isStatusLoaded, setIsStatusLoaded] = useState(false);

  const isFetchingRef = useRef(false);

  const {
    mallOptions,
    tenantOptions,
    selectedMall,
    selectedTenant,
    setSelectedMall,
    setSelectedTenant,
  } = useMallTenantDropdown();

  const fetchTenantRequests = useCallback(
    async (
      tenantid: number,
      taskid: number,
      mallid: number,
      tenantrequestid: number,
      requeststatusid: number,
      number: number,
      pageNum: number
    ) => {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        const response = await GetRequestTenant(
          tenantid,
          taskid,
          mallid,
          tenantrequestid,
          requeststatusid,
          number,
          pageNum
        );
        if (
          response &&
          response.requestTenantDatas &&
          Array.isArray(response.requestTenantDatas)
        ) {
          setTenantRequestData(response.requestTenantDatas);
        } else {
          setTenantRequestData([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setTenantRequestData([]);
        await Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch tenant requests",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } finally {
        isFetchingRef.current = false;
      }
    },
    []
  );

  const getStatusLabelById = useCallback(
    (statusId: number | undefined | null, rawStatusName?: string): string => {
      if (!statusId && !rawStatusName) return "";
      const id = Number(statusId ?? 0);
      if (id === 70) return formatStatusLabel("Request Rejected");
      const option = requestStatusOptions.find(
        (opt) => Number(opt.value) === id
      );
      const raw = option ? option.label : (rawStatusName || "");
      return formatStatusLabel(raw);
    },
    [requestStatusOptions]
  );

  const stats = useMemo(() => {
    const statusCounts: Record<string, number> = {};
    requestStatusOptions.forEach((opt) => {
      statusCounts[opt.label] = 0;
    });
    tenantRequestData.forEach((req) => {
      const statusId = Number(req.RequestStatusId ?? 0);
      const opt = requestStatusOptions.find(
        (o) => Number(o.value) === statusId
      );
      if (opt) statusCounts[opt.label] = (statusCounts[opt.label] || 0) + 1;
    });
    return statusCounts;
  }, [tenantRequestData, requestStatusOptions]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    let arr = tenantRequestData || [];

    if (
      selectedRequestStatus !== null &&
      typeof selectedRequestStatus !== "undefined"
    ) {
      arr = arr.filter(
        (t) => Number(t.RequestStatusId) === Number(selectedRequestStatus)
      );
    }

    if (!qq) return arr;
    return arr.filter((t) => {
      const statusLabel = getStatusLabelById(t.RequestStatusId);
      return (
        (t.RequestNumber || "").toLowerCase().includes(qq) ||
        (t.MallName || "").toLowerCase().includes(qq) ||
        (t.TenantName || "").toLowerCase().includes(qq) ||
        (t.Notes || "").toLowerCase().includes(qq) ||
        (t.RequestStatusName || "").toLowerCase().includes(qq) ||
        statusLabel.toLowerCase().includes(qq)
      );
    });
  }, [q, tenantRequestData, selectedRequestStatus, getStatusLabelById]);

  const sorted = useMemo(() => {
    const arr = Array.isArray(filtered) ? filtered : [];
    return [...arr].sort((a, b) => b.TenantRequestId - a.TenantRequestId);
  }, [filtered]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * perPage;
  const end = Math.min(start + perPage, total);
  const pageRows = sorted.slice(start, end);

  const openAddModal = () => {
    setShowAddModal(true);
    setAddModalMall(null);
    setAddModalTenant(null);
    setAddModalNotes("");
    setAddModalImages([]);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddModalMall(null);
    setAddModalTenant(null);
    setAddModalNotes("");
    setAddModalImages([]);
    setModalMallOptions([]);
    setModalTenantOptions([]);
  };

  const convertImageToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmitAddRequest = async () => {
    if (!addModalMall || !addModalTenant) {
      await Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Please select Mall and Tenant",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const imageDataArray: ImageData[] = [];

      console.log("Converting images, total:", addModalImages.length);

      for (let i = 0; i < addModalImages.length; i++) {
        const file = addModalImages[i];
        const base64Data = await convertImageToBase64(file);

        const imageData: ImageData = {
          TenantRequestImageId: 0,
          TenantRequestId: 0,
          ImageTypeId: 1,
          ImageTypeName: "Request Image",
          FileImage: file.name,
          DataImage: base64Data,
          ImageLabel: file.name,
          Status: 1,
        };

        imageDataArray.push(imageData);
        console.log(`Image ${i + 1} converted:`, {
          name: file.name,
          size: file.size,
          base64Length: base64Data.length,
        });
      }

      const selectedMallData = modalMallOptions.find(
        (m) => m.value === addModalMall
      );
      const selectedTenantData = modalTenantOptions.find(
        (t) => t.value === addModalTenant
      );

      const payload = {
        requestTenantData: {
          TenantRequestId: 0,
          TaskId: 0,
          RequestNumber: null,
          MallId: addModalMall,
          MallName: selectedMallData?.label || "",
          TenantId: addModalTenant,
          TenantName: selectedTenantData?.label || "",
          TenantRequestName: `Request from ${selectedTenantData?.label || ""}`,
          Notes: addModalNotes,
          tenantRequestImageDatas: imageDataArray,
          RequestStatusId: 10,
          Status: 1,
          CreatedOn: toLocalISOString(new Date()),
        },
      };

      console.log("Payload being sent:", JSON.stringify(payload, null, 2));
      console.log(
        "Image data count in payload:",
        payload.requestTenantData.tenantRequestImageDatas.length
      );

      const response = await SaveRequestTenant(payload);

      console.log("API Response:", response);

      if (response) {
        await Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Request created successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });

        closeAddModal();
        if (selectedTenant && selectedMall) {
          fetchTenantRequests(
            selectedTenant,
            taskId,
            selectedMall,
            tenantRequestId,
            0,
            numberParam,
            0
          );
        }
      } else {
        throw new Error("Failed to create request");
      }
    } catch (error) {
      console.error("Error creating request:", error);
      await Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to create request",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const onExport = () => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Tenant Request Report", 14, 15);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")}`, 14, 22);

      const tableData = (Array.isArray(sorted) ? sorted : []).map((row, idx) => [
        idx + 1,
        row.RequestNumber,
        row.MallName,
        row.TenantName,
        row.Notes || "-",
        getStatusLabelById(row.RequestStatusId, row.RequestStatusName) || "-",
      ]);

      autoTable(doc, {
        head: [
          [
            "No",
            "Request Number",
            "Mall",
            "Tenant",
            "Notes",
            "Status",
          ],
        ],
        body: tableData,
        startY: 28,
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 12, halign: "center" },
          1: { cellWidth: 40 },
          2: { cellWidth: 35 },
          3: { cellWidth: 35 },
          4: { cellWidth: 50 },
          5: { cellWidth: 25, halign: "center" },
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        margin: { top: 28, left: 14, right: 14 },
      });

      const fileName = `Tenant_Requests_${toLocalISOString(new Date()).split("T")[0]}.pdf`;
      doc.save(fileName);

      Swal.fire({
        icon: "success",
        title: "PDF Export Success",
        text: `File ${fileName} has been downloaded`,
        timer: 2000,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "PDF Export Failed",
        text: String(error),
      });
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await GetRequestStatusTenant(0);
        const arr =
          (res && res.dropdownClassDatas) ||
          (res && res.data && res.data.dropdownClassDatas) ||
          (Array.isArray(res) ? res : []);

        const options: DropdownOption[] = Array.isArray(arr)
          ? arr.map((it: RawStatusData) => ({
            value: Number(it.DataId ?? it.dataId ?? it.value ?? 0),
            label:
              it.DataLabel ??
              it.dataLabel ??
              it.label ??
              String(it as unknown),
          }))
          : [];

        console.log("Loaded status options:", options);
        setRequestStatusOptions(options);
        setIsStatusLoaded(true);
      } catch (e) {
        console.error("Error loading request status options:", e);
        setIsStatusLoaded(true);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch request status options",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadModalMallOptions = async () => {
      try {
        const res = await fetchdropdownMall(0);
        if (res && res.dropdownClassDatas) {
          const options: DropdownOption[] = res.dropdownClassDatas.map(
            (it: { DataId: number; DataLabel: string }) => ({
              value: Number(it.DataId),
              label: it.DataLabel,
            })
          );
          setModalMallOptions(options);
        }
      } catch (e) {
        console.error("Error loading modal mall options:", e);
      }
    };
    if (showAddModal) loadModalMallOptions();
  }, [showAddModal]);

  useEffect(() => {
    if (
      modalMallOptions.length === 1 &&
      addModalMall === null &&
      showAddModal
    ) {
      setAddModalMall(modalMallOptions[0].value);
    }
  }, [modalMallOptions, addModalMall, showAddModal]);

  useEffect(() => {
    const loadModalTenantOptions = async () => {
      if (addModalMall) {
        try {
          const res = await fetchdropdownTenant(addModalMall);
          if (res && res.dropdownClassDatas) {
            const options: DropdownOption[] = res.dropdownClassDatas.map(
              (it: { DataId: number; DataLabel: string }) => ({
                value: Number(it.DataId),
                label: it.DataLabel,
              })
            );
            setModalTenantOptions(options);
          }
        } catch (e) {
          console.error("Error loading modal tenant options:", e);
          setModalTenantOptions([]);
        }
      } else {
        setModalTenantOptions([]);
        setAddModalTenant(null);
      }
    };
    if (showAddModal) loadModalTenantOptions();
  }, [addModalMall, showAddModal]);

  useEffect(() => {
    if (
      modalTenantOptions.length === 1 &&
      addModalTenant === null &&
      showAddModal
    ) {
      setAddModalTenant(modalTenantOptions[0].value);
    }
  }, [modalTenantOptions, addModalTenant, showAddModal]);

  useEffect(() => {
    if (
      selectedTenant &&
      selectedMall &&
      selectedTenant > 0 &&
      selectedMall > 0 &&
      isStatusLoaded
    ) {
      console.log("Fetching tenant requests with status options loaded");
      fetchTenantRequests(
        selectedTenant,
        taskId,
        selectedMall,
        tenantRequestId,
        0,
        numberParam,
        0
      );
    }
  }, [
    selectedTenant,
    selectedMall,
    isStatusLoaded,
    fetchTenantRequests,
    taskId,
    tenantRequestId,
    numberParam,
  ]);

  useEffect(() => {
    setPage(1);
  }, [q, selectedRequestStatus]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-3 my-4">
        <div className="flex items-center gap-3">
          <div className="w-full sm:min-w-[320px] sm:min-h-[36px] flex items-center">
            <Dropdown
              mallSelected={setSelectedMall}
              mallOptions={mallOptions}
              tenantSelected={setSelectedTenant}
              tenantOptions={tenantOptions}
              mallValue={selectedMall}
              tenantValue={selectedTenant}
            />
          </div>
        </div>

        <div className="flex flex-col w-full sm:w-auto">
          <label
            htmlFor="requestStatus"
            className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
            style={{ transform: "translateY(2px)" }}
          >
            Request Status
          </label>
          <select
            id="requestStatus"
            className={`block w-full sm:w-48 px-3 text-sm h-9 rounded-md border shadow-sm transition-all ${requestStatusOptions.length === 1
              ? "bg-white text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700"
              : "bg-white text-gray-700 border-gray-200 hover:border-blue-400 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:border-blue-500"
              } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            onChange={(e) => {
              const val =
                e.target.value === "" ? null : Number(e.target.value);
              setSelectedRequestStatus(val);
              setPage(1);
            }}
            value={selectedRequestStatus ?? ""}
          >
            <option value="">All Status</option>
            {requestStatusOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {formatStatusLabel(item.label)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="min-h-screen bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl">
        <div className="hidden sm:block">
          <TenantRequestStats
            requestStatusOptions={requestStatusOptions}
            stats={stats}
          />
        </div>

        <div className="p-4 rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Request List
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={openAddModal}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
              <button
                onClick={onExport}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z" />
                </svg>
                PDF
              </button>
            </div>
          </div>

          <div className="relative w-full sm:w-80 mb-4">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hidden sm:flex">
              <Search className="w-4 h-4" />
            </span>
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search for requests"
              className="w-full sm:pl-9 pl-3 pr-3 py-2 rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 placeholder:dark:text-gray-500 text-sm"
            />
          </div>

          <div className="block sm:hidden">
            {pageRows.length === 0 ? (
              <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">
                No data found.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {pageRows.map((row) => {
                  const statusLabel = getStatusLabelById(row.RequestStatusId, row.RequestStatusName);
                  return (
                    <div
                      key={row.TenantRequestId}
                      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex flex-col gap-2 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                          {row.RequestNumber || "-"}
                        </span>
                        {statusLabel && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 font-medium">
                            {statusLabel}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="flex gap-2 text-sm">
                          <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">
                            Mall
                          </span>
                          <span className="text-gray-800 dark:text-gray-100 font-medium">
                            {row.MallName || "-"}
                          </span>
                        </div>
                        <div className="flex gap-2 text-sm">
                          <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">
                            Tenant
                          </span>
                          <span className="text-gray-800 dark:text-gray-100 font-medium">
                            {row.TenantName || "-"}
                          </span>
                        </div>
                        {row.Notes && (
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">
                              Notes
                            </span>
                            <span className="text-gray-600 dark:text-gray-300 break-all">
                              {row.Notes}
                            </span>
                          </div>
                        )}
                        {row.CreatedOn && (
                          <div className="flex gap-2 text-sm">
                            <span className="text-gray-400 dark:text-gray-500 w-16 shrink-0">
                              Date
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              {new Date(row.CreatedOn).toLocaleDateString(
                                "id-ID",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {total === 0 ? "0" : `${start + 1}–${end}`} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={clampedPage <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <span className="text-xs text-gray-700 dark:text-gray-200 font-medium">
                  {clampedPage} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={clampedPage >= totalPages}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div className="hidden sm:block">
            <TenantRequestTable
              pageRows={pageRows}
              selected={selected}
              setSelected={setSelected}
              sortKey={sortKey}
              sortDir={sortDir}
              toggleSort={toggleSort}
              getStatusLabelById={getStatusLabelById}
              sorted={sorted}
              perPage={perPage}
              setPerPage={setPerPage}
              page={page}
              setPage={setPage}
              clampedPage={clampedPage}
              totalPages={totalPages}
              start={start}
              end={end}
            />
          </div>
        </div>
      </div>

      <AddRequestModal
        showAddModal={showAddModal}
        closeAddModal={closeAddModal}
        addModalMall={addModalMall}
        setAddModalMall={setAddModalMall}
        addModalTenant={addModalTenant}
        setAddModalTenant={setAddModalTenant}
        addModalNotes={addModalNotes}
        setAddModalNotes={setAddModalNotes}
        addModalImages={addModalImages}
        setAddModalImages={setAddModalImages}
        modalMallOptions={modalMallOptions}
        modalTenantOptions={modalTenantOptions}
        isSubmitting={isSubmitting}
        handleSubmitAddRequest={handleSubmitAddRequest}
      />
    </>
  );
}